/**
 * showToast — Global toast helper for Xyden.
 *
 * Works anywhere (event handlers, async callbacks, outside React components).
 * Requires <Toaster /> mounted once in App.tsx.
 *
 * Usage:
 *   showToast({ variant: "success", title: "Saved", description: "Changes saved." })
 *   showToast({ variant: "error", title: "Error", description: "Something went wrong." })
 *   showToast({ variant: "warning", title: "Warning", description: "Check your input." })
 *   showToast({ variant: "info", title: "Info", description: "New version available." })
 *   showToast({ variant: "success", title: "Deleted", action: { label: "Undo", onClick: handleUndo } })
 */

import { toast } from "sonner";
import { createElement } from "react";
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from "lucide-react";

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ShowToastOptions {
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const variantConfig: Record<ToastVariant, { icon: typeof CheckCircle; bg: string }> = {
  success: {
    icon: CheckCircle,
    bg: "bg-green-600",
  },
  error: {
    icon: AlertCircle,
    bg: "bg-brand-crimson",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-brand-active",
  },
  info: {
    icon: Info,
    bg: "bg-blue-600",
  },
};

export function showToast({
  variant,
  title,
  description,
  duration = 5000,
  dismissible = true,
  action,
}: ShowToastOptions) {
  const config = variantConfig[variant];

  toast.custom(
    (id) =>
      createElement(
        "div",
        {
          className: `flex items-center gap-3 w-full ${config.bg} rounded-lg shadow-sm px-4 py-3 min-w-[340px]`,
        },
        // Icon
        createElement(config.icon, {
          size: 20,
          className: "text-white shrink-0",
          "aria-hidden": "true",
        }),
        // Content
        createElement(
          "div",
          { className: "flex-1 min-w-0" },
          createElement("p", { className: "text-sm font-semibold text-white" }, title),
          description ? createElement("p", { className: "text-xs text-white/80 mt-0.5" }, description) : null,
          action
            ? createElement(
                "button",
                {
                  type: "button",
                  className:
                    "text-xs font-semibold text-white underline underline-offset-2 hover:text-white/80 mt-1 transition-colors",
                  onClick: () => {
                    action.onClick();
                    toast.dismiss(id);
                  },
                },
                action.label
              )
            : null
        ),
        // Close button
        dismissible
          ? createElement(
              "button",
              {
                type: "button",
                className: "text-white/70 hover:text-white shrink-0 transition-colors",
                "aria-label": "Dismiss",
                onClick: () => toast.dismiss(id),
              },
              createElement(X, { size: 20, strokeWidth: 2.5 })
            )
          : null
      ),
    {
      duration,
      dismissible,
    }
  );
}
